import { useCallback } from "react";

import { useProgramTemplates } from "containers/admin/clients/client/program-templates-page/hooks/use-program-templates";

export const PROGRAM_TEMPLATE_NAME_TAKEN_ERROR = "Template with this name already exists";

interface UseValidateProgramTemplateNameOptions {
  /** Only fetch templates while the caller (e.g. a modal) is open. */
  enabled?: boolean;
}

interface ValidateArgs {
  name: string;
  /** Template type to scope the uniqueness check (e.g. "EMAIL" vs "LANDING_PAGE"). */
  type?: string;
  /** Template id to exclude from the check (useful when renaming an existing template). */
  excludeId?: string;
}

/**
 * Returns a validator that checks whether a program template name is already
 * taken by another template of the same type. Intended for use with
 * react-hook-form `rules.validate`.
 */
export const useValidateProgramTemplateName = ({
  enabled,
}: UseValidateProgramTemplateNameOptions = {}) => {
  const { programTemplates } = useProgramTemplates({}, { enabled });

  const validate = useCallback(
    ({ name, type, excludeId }: ValidateArgs): string | true => {
      const normalized = name.trim().toLowerCase();
      const isNameTaken = programTemplates?.some(
        (template) =>
          template.id !== excludeId &&
          template.name?.toLowerCase() === normalized &&
          template.type === type,
      );

      return isNameTaken ? PROGRAM_TEMPLATE_NAME_TAKEN_ERROR : true;
    },
    [programTemplates],
  );

  return { validate, programTemplates };
};
